import React, { useState, useEffect } from 'react';

import CloseIcon from '@mui/icons-material/ClearRounded';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import QIllustration from 'components/QIllustration';
import LoadingView from 'components/LoadingView';
import type { Timeout } from 'react-number-format/types/types';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 350,
    backgroundColor: theme.palette.background.paper,
    overflow: 'auto',
  },
  closeButton: {
    color: theme.palette.icon.dark,
    position: 'absolute',
    top: 12,
    right: 12,
    transition: 'all 0.4s ease',
  },
  successText: {
    minHeight: 48,
    maxWidth: 173,
    textAlign: 'center',
  },
}));

const margin = { margin: '64px 48px' };

let closeButtonTimer: Timeout | null;
let autoCloseTimer: Timeout | null;

interface LoadingDialogProps {
  onClose: () => void;
  loading: boolean;
  open: boolean;
  loadingString: string;
  successString: string | null;
}

function LoadingDialog({ onClose, loading, loadingString, successString, open, ...otherProps }: LoadingDialogProps) {
  const { classes } = useStyles();

  const [showCloseButton, setShowCloseButton] = useState(false);

  useEffect(() => {
    closeButtonTimer = setTimeout(() => {
      setShowCloseButton(true);
      closeButtonTimer = null;
    }, 5000);

    return () => {
      // cleanup
      if (closeButtonTimer) {
        clearTimeout(closeButtonTimer);
      }
    };
  }, []);

  useEffect(() => {
    if (successString) {
      autoCloseTimer = setTimeout(() => {
        autoCloseTimer = null;
        onClose();
      }, 1200);
    }

    return () => {
      // cleanup
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
    };
  }, [successString]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog maxWidth='xs' fullWidth onClose={onClose} open={open} {...otherProps}>
      <div className={classes.root}>
        <IconButton
          aria-label='Close'
          id='dlg-close'
          onClick={onClose}
          className={classes.closeButton}
          disabled={!showCloseButton}
          style={{ opacity: showCloseButton ? 1 : 0, height: showCloseButton ? undefined : 0 }}
        >
          <CloseIcon />
        </IconButton>

        {successString ? (
          <>
            <QIllustration name='successCheck' />
            <Typography variant='titleRegular' className={classes.successText}>
              {successString}
            </Typography>
          </>
        ) : (
          <LoadingView title={loadingString} size={65} style={margin} />
        )}
      </div>
    </Dialog>
  );
}

export default React.memo(LoadingDialog);
